import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, Database, Target, BookOpen, GraduationCap, Radio, BarChart3, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Tilt3D } from "../components/Tilt3D";

// Hub di Sales Help: tutte le funzionalità del modulo in un'unica pagina
const FEATURES = [
  { to: "/aggiungi", title: "Aggiungi contenuto", icon: PlusCircle, accent: "#60A5FA", id: "help-add",
    descr: "Carica testi, PDF, Word, foto, audio/video o link social: l'AI estrae script e linee guida di vendita." },
  { to: "/conoscenza", title: "Knowledge Base", icon: Database, accent: "#3B82F6", id: "help-kb",
    descr: "La conoscenza organizzata per fase della trattativa → intento → cluster, con revisione e ricerca semantica.", stat: (o) => `${o?.conoscenze ?? "—"} conoscenze · ${o?.script ?? "—"} script` },
  { to: "/contesto", title: "Contesto", icon: Target, accent: "#10B981", id: "help-context",
    descr: "Settore, canale, cliente tipo e tono: calibra suggeritore, coach e agente vocale sul tuo mercato." },
  { to: "/manuale", title: "Manuale script", icon: BookOpen, accent: "#A78BFA", id: "help-manual",
    descr: "Il manuale costruito dalla tua KB: frasi pronte per ogni fase, con modalità memoria per allenarti." },
  { to: "/coach", title: "Coach", icon: GraduationCap, accent: "#F472B6", id: "help-coach",
    descr: "Chiedi al coach come gestire una situazione: risponde con la frase pronta e le fonti dalla tua KB." },
  { to: "/live", title: "Assistente Live", icon: Radio, accent: "#F5B942", id: "help-live", accentCard: true,
    descr: "Durante la call ascolta cliente e operatore e suggerisce in tempo reale cosa dire.", stat: (o) => `${o?.call_live ?? "—"} call analizzate` },
  { to: "/live/panoramica", title: "Panoramica call", icon: BarChart3, accent: "#FCD990", id: "help-analytics",
    descr: "Metriche aggregate delle call: punteggi, tempi morti, obiezioni ricorrenti, priorità di miglioramento." },
];

export default function SalesHelp() {
  const [ov, setOv] = useState(null);
  useEffect(() => {
    api.get("/overview").then(({ data }) => setOv(data.sales_help)).catch(() => {});
  }, []);

  return (
    <div data-testid="sales-help-page" className="space-y-8">
      <div className="fade-up">
        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Modulo</p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">Sales <span className="text-primary">Help</span></h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Tutto ciò che serve per allenare e assistere la vendita: dalla conoscenza caricata, al manuale, al coach, fino al suggeritore in tempo reale durante le call.
        </p>
      </div>

      <div className="stagger persp grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map(({ to, title, icon: Icon, accent, descr, stat, id, accentCard }) => (
          <Link key={to} to={to} data-testid={id} className="block">
            <Tilt3D className={`surface stat-3d flex h-full flex-col p-5 transition-colors hover:border-white/20 ${accentCard ? "border-dashed border-[#F5B942]/40" : ""}`}>
              <span className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ background: `${accent}22`, color: accent }}><Icon className="h-5 w-5" /></span>
              <h2 className="mt-4 text-base font-semibold md:text-lg">{title}</h2>
              <p className="mt-1.5 flex-1 text-sm leading-relaxed text-muted-foreground">{descr}</p>
              <div className="mt-4 flex items-center justify-between text-xs">
                <span className="font-mono text-muted-foreground">{stat ? stat(ov) : ""}</span>
                <span className="inline-flex items-center gap-1 font-semibold" style={{ color: accent }}>Apri <ArrowRight className="h-3.5 w-3.5" /></span>
              </div>
            </Tilt3D>
          </Link>
        ))}
      </div>
    </div>
  );
}
